(async() => {
    const { Core } = await importModule('Core');
    const {
        $JSON,
        cache,
        $fetch
    } = await importModule('Utils');

    const CACHE_KEY = 'variant-provider';

    function elementsToMap(elements) {
        return elements.reduce((acc, element) => {
            if (element.id) {
                acc[element.id] = element.cloneNode(true);
            }
            return acc;
        }, {});
    }

    class OptionsHistory {
        constructor(limit) {
            this.limit = limit;
            this.items = Array.from(Array(limit).keys());
        }
        add(item) {
            if(this.lastItem === item) {
                return;
            }

            const index = this.items.indexOf(item);
            this.items.splice(index, 1);
            this.items.push(item);
            // sanity chech
            if (this.items.length > this.limit) {
                this.items.shift();
            }
        }
        get lastItem() {
            return this.items.at(-1);
        }

        getValidOption(options) {
            for (const optionIndex of this.items) {
                // If matching option index comes from the last item, it means selection
                // from other(previous) options failed to find it, hence the next selection
                // will not include a variant with the last item option id
                if(options[optionIndex] && this.lastItem !== optionIndex) {
                    return {
                        ...options[optionIndex],
                        optionIndex
                    }
                }
            }
            return null;
        }
    }

    customElements.define('product-variant-provider', class extends Core {
        elements = {
            productMeta: '[data-product-meta]',
            listeners: '*product-variant-listener'
        }

        subscriptions = {
            'selling-plan:change': '_onSellingPlanChange',
            'variant:change': '_changeVariant',
        }

        render() {
            this.smartSelect = !this.hasAttribute('allow-unavailable-variants');
            this.disableCache = this.hasAttribute('no-cache');
            this.subscribe('variant:change');
            this.subscribe('selling-plan:change');
            this._initState();
        }

        async _initState() {
            this.currentState = {
                variantId: this.initialVariantId,
                optionValueIds: this.initialOptionsState.split(','),
                variantImagePosition: this.initialImagePosition,
                sellingPlanId: this.initialSellingPlan
            };
            if (this.smartSelect) {
                this.optionsHistory = new OptionsHistory(this.currentState.optionValueIds.length);
            }
            // Track which option positions the shopper has explicitly chosen by
            // clicking. Nothing is "touched" on initial load, so the picker starts
            // blank: no option pre-checked, and no option value shown as unavailable.
            // Positions that "smart select" silently fills in behind the scenes
            // (see _smartSelectVariant) are NOT marked touched, so they stay
            // visually neutral until the shopper actually picks them.
            this.touchedPositions = new Set();
            // IMPORTANT: seed the cache from the pristine, server-rendered markup
            // BEFORE _enforceSelectionState() runs. _enforceSelectionState() disables
            // the buy button and swaps its label to "Select options" on the live DOM
            // for the untouched state - if we cached AFTER that call, the very first
            // cache entry (keyed by the initial variant id + option values, with no
            // regard for touched state) would permanently bake in the disabled
            // snapshot. Later, when a shopper touches an option position without
            // actually changing its value (most commonly: the default/first
            // variant's own option, e.g. clicking "S" when "S" was already the
            // pre-selected default on a single-option product), the resulting cache
            // key is identical to this initial one, so the stale disabled markup
            // gets replayed instead of a fresh fetch - leaving Add to Cart stuck on
            // "Select options" forever for that value. Caching the clean markup
            // first fixes that without touching the enforcement logic itself.
            this._initCache();
            this._enforceSelectionState();
            this._observeMutations();
        }

        // The section-rendering-API listeners (product-variant-listener) swap in
        // fresh server-rendered markup asynchronously and can do so multiple times
        // in a chain (smart select resolving a full variant behind the scenes).
        // That server markup has no concept of "touched" state, so every time it
        // lands it can silently overwrite our checked/disabled overrides. Rather
        // than guessing at timing with setTimeout, watch the DOM directly and
        // re-apply our overrides the instant anything changes underneath us.
        _observeMutations() {
            if (this._mutationObserver) {
                return;
            }
            this._mutationObserver = new MutationObserver(() => {
                if (this._enforcing) {
                    return;
                }
                this._enforceSelectionState();
            });
            // Scope to just the listener wrappers (variant_selector / buy_buttons
            // blocks) that actually get their children replaced, NOT the whole
            // provider element - that also wraps the media gallery, which mutates
            // on its own (lazy-loaded images, zoom, etc.) and would fire this
            // observer constantly for reasons unrelated to variant selection.
            (this.$listeners || []).forEach((listenerEl) => {
                this._mutationObserver.observe(listenerEl, { childList: true, subtree: true });
            });
        }

        _initCache() {
            if(this.disableCache) {
                return;
            }
            if (!cache.has(this._cacheKey)) {
                cache.set(this._cacheKey, {
                    listeners: elementsToMap(this.$listeners)
                });
                return;
            }
            // if we have cache on initilization it's mean this cache was initited by someone before
            // refresh contents on the next tick
            setTimeout(() => {
                this._publishUpdates();
            }, 20)
        }

        _changeVariant({ optionValueId, optionIndex, variantId, imagePosition }, { userInitiated = true } = {}) {
            this.currentState.optionValueIds[+optionIndex] = optionValueId;
            this.currentState.variantId = variantId || undefined;
            this.currentState.variantImagePosition = imagePosition;

            if (userInitiated) {
                this.touchedPositions.add(+optionIndex);
            }
            this.optionsHistory?.add(+optionIndex);
            this._handleUpdate();
        }

        async _handleUpdate() {
            await this._updateCache();
            this._publishUpdates();
            this._updateProps();
            this._enforceSelectionState();
            if (this.isProductPage) {
                this._updateBrowserHistory();
            }
        }

        // Keeps the picker from ever showing a pre-selected value or graying out
        // an option before the shopper has actually clicked something. Options the
        // shopper hasn't touched yet stay neutral (unchecked, ungrayed) even though
        // the server (and smart-select) may resolve a "best guess" full variant
        // behind the scenes for pricing/media purposes.
        // Add to Cart stays disabled with a "Select options" label until every
        // option position has been explicitly chosen.
        _enforceSelectionState() {
            // Reentrancy guard: this method mutates the DOM, which can retrigger
            // the MutationObserver below. Skip if we're already inside a call.
            if (this._enforcing) {
                return;
            }
            this._enforcing = true;
            try {
                const inputs = Array.from(this.querySelectorAll('input[data-option-index]'));
                const positions = new Set(inputs.map((input) => +input.dataset.optionIndex));

                inputs.forEach((input) => {
                    const idx = +input.dataset.optionIndex;
                    if (this.touchedPositions.has(idx)) {
                        // Force this input's checked state to match our own record of
                        // what the shopper picked, rather than trusting whatever the
                        // freshly-swapped-in server markup happens to carry — smart
                        // select can trigger multiple chained re-renders per click,
                        // and relying on passive attribute reflection across those
                        // proved unreliable.
                        input.checked = input.dataset.optionValueId === this.currentState.optionValueIds[idx];
                    } else {
                        input.checked = false;
                    }
                });

                this.querySelectorAll('[data-display-value]').forEach((el) => {
                    const idx = +el.dataset.optionIndex;
                    if (!this.touchedPositions.has(idx)) {
                        el.textContent = '';
                    }
                });

                if (this.touchedPositions.size === 0) {
                    inputs.forEach((input) => {
                        input.classList.remove('!unavailable', '!sold-out');
                    });
                }

                const allTouched = positions.size > 0 && this.touchedPositions.size >= positions.size;
                const buyBtn = this.querySelector('[data-to-cart]');
                if (buyBtn && positions.size > 0 && !allTouched) {
                    buyBtn.disabled = true;
                    if (buyBtn.getAttribute('aria-disabled') !== 'true') {
                        buyBtn.setAttribute('aria-disabled', 'true');
                    }
                    // IMPORTANT: `.textContent = x` always creates a brand-new text
                    // node, even when x is unchanged - and that counts as a DOM
                    // mutation. Since the MutationObserver below re-runs this method
                    // on every mutation inside the buy-button's wrapper, writing
                    // unconditionally here creates an infinite mutate -> observe ->
                    // mutate loop that freezes the tab. Only write when it actually
                    // needs to change.
                    const desiredText = window.themeStrings?.selectOptions || 'Select options';
                    if (buyBtn.textContent.trim() !== desiredText) {
                        buyBtn.textContent = desiredText;
                    }
                }
            } finally {
                this._enforcing = false;
            }
        }

        async _updateCache() {
            if (cache.has(this._cacheKey) && !this.disableCache) return;
            const $listeners = await this._fetchDoc();
            if($listeners) {
                cache.set(this._cacheKey, {
                    listeners: elementsToMap($listeners),
                });
            }
        }

        _updateProps() {
            this.setAttribute('initial-variant-id', this.currentState.variantId);
            this.setAttribute('initial-image-position', this.currentState.variantImagePosition);
            this.setAttribute('initial-selling-plan', this.currentState.sellingPlanId);
        }

        _publishUpdates() {
            if(cache.has(this._cacheKey)) {
                this.publish('variant:update', {
                    imagePosition: this.currentState.variantImagePosition,
                    targets: cache.get(this._cacheKey).listeners
                })
            }
        }

        get _cacheKey() {
            return `${CACHE_KEY}-${this.sectionId}-${this.currentState.variantId}-${this.optionValueIdsString}-${this.currentState.sellingPlanId}`;
        }

        get optionValueIdsString() {
            return this.currentState.optionValueIds.toString();
        }

        async _fetchDoc() {
            const $doc = await $fetch(this.productURL, {
                before: this._loading(true),
                after: this._loading(false),
                params: {
                    selling_plan: this.currentState.sellingPlanId,
                    section_id: this.sectionId,
                    option_values: this.optionValueIdsString,
                },
                select: `#${this.id}`
            })

            if (this.smartSelect && !this.currentState.variantId) {
                const $availableOptionsJSON = $doc.querySelector('[data-available-options-json]');
                if (this._smartSelectVariant($availableOptionsJSON)) {
                    // We will get a doc from the next _fetch iteration initiated by smart select
                    return null;
                }
            }
            return this._getListenersFromDoc($doc);
        }


        _getListenersFromDoc($doc) {
            return Array.from($doc.querySelectorAll('product-variant-listener'));
        }

        _loading(state) {
            return () => {
                this.publish('variant:loading', state);
            }
        }

        _smartSelectVariant(optionsJSON) {
            if(!optionsJSON) {
                console.error('JSON schema for smart select variants is missing');
                return false;
            }

            let options;
            try {
                options = $JSON(optionsJSON);
            } catch (e) {
                console.error(e);
                return false;
            }

            const matchOption = this.optionsHistory.getValidOption(options);

            if(!matchOption) {
                return false;
            }

            // Smart select is filling in an option the shopper hasn't actually
            // clicked yet (to resolve a variant for price/media purposes), so
            // don't mark this position as user-touched.
            this._changeVariant(matchOption, { userInitiated: false })
            return true;

        }

        _updateBrowserHistory() {
            window.history.replaceState({}, null, `${window.location.pathname}?${this.browserHistoryURLParams.toString()}`);
        }

        _onSellingPlanChange({ sellingPlanId }) {
            this.currentState.sellingPlanId = sellingPlanId;
            this._handleUpdate();
        }

        get browserHistoryURLParams() {
            const URLParams = new URLSearchParams({});
            if (this.currentState.variantId) {
                URLParams.set('variant', this.currentState.variantId);
            }
            // if (!this.currentState.variantId) { //NOTE: leave this here for now. The Dawn avoids this.
            //     URLParams.set('option_values', this.optionValueIdsString);
            // }
            this.currentState.sellingPlanId ? URLParams.set('selling_plan', this.currentState.sellingPlanId) : URLParams.delete('selling_plan');
            return new URLSearchParams(URLParams);
        }

        get isProductPage() {
            return this.hasAttribute('product-page');
        }
        get productURL() {
            return this.getAttribute('product-url');
        }
        get initialVariantId() {
            return this.getAttribute('initial-variant-id');
        }
        get initialImagePosition() {
            return this.getAttribute('initial-image-position');
        }
        get initialSellingPlan() {
            return this.getAttribute('initial-selling-plan');
        }
        get initialOptionsState() {
            return this.getAttribute('initial-options-state');
        }

    });
})()
